import axios, { AxiosPromise, AxiosRequestConfig } from 'axios';
import {
  ArquivoEntity,
  ArquivoMultipartUrl,
  ArquivoParteUpload,
  ArquivoUrl,
  AssinaturaDigital,
  ModeloArquivoTemplateDTO
} from 'common/type';

import { TipoSigapEnum } from '../type/enum';

export default class ArquivoService {
  resource: string;
  baseUrl: string;

  constructor(public _baseUrl: string, _resource: string) {
    this.baseUrl = _baseUrl;
    this.resource = _baseUrl + _resource;
  }

  public buscarArquivos = (entityId: string): AxiosPromise<ArquivoEntity[]> =>
    axios.get<ArquivoEntity[]>(`${this.resource}/${entityId}/arquivos`);

  public verificarAssinaturasDigitais = (
    entityId: string
  ): AxiosPromise<Record<string, AssinaturaDigital[]>> =>
    axios.get<Record<string, AssinaturaDigital[]>>(
      `${this.resource}/${entityId}/arquivos/assinaturas-digitais`
    );

  public buscarArquivosPorTipo = (
    entityId: string,
    tipo: string
  ): AxiosPromise<ArquivoEntity[]> => {
    /*
     *   Parâmetro tipo está codificado em Base 64, por conta dos caracteres especiais que podem estar presentes.
     *   Exemplo: text/html, application/xhtml+xml, caractere especial = "/", "+"
     */
    const tipoEncode: string = btoa(tipo);
    return axios.get<ArquivoEntity[]>(
      `${this.resource}/${entityId}/arquivos/by-tipo/${tipoEncode}`
    );
  };

  public verificarAssinaturasDigitaisPorTipo = (
    entityId: string,
    tipo: string
  ): AxiosPromise<Record<string, AssinaturaDigital[]>> => {
    const tipoEncode = btoa(tipo);

    return axios.get<Record<string, AssinaturaDigital[]>>(
      `${this.resource}/${entityId}/arquivos/by-tipo/${tipoEncode}/assinaturas-digitais`
    );
  };

  public getMaxSize = (): AxiosPromise<number> => {
    return axios.get(`${this.baseUrl}/arquivos/max-size`);
  };

  public gerarUrlParaUpload = (
    filePath: number,
    fileName: string,
    contentType: string,
    configs?: AxiosRequestConfig
  ): AxiosPromise<ArquivoUrl> => {
    return axios.post(
      `${
        this.resource
      }/arquivos/gerar-url?filePath=${filePath}&fileName=${encodeURIComponent(
        fileName
      )}&contentType=${contentType}`,
      null,
      configs
    );
  };

  public enviarArquivoParaServidor = (
    file: File,
    url: string,
    configs?: AxiosRequestConfig
  ) => {
    return axios.put(url, file, configs);
  };

  public gerarUrlsParaMultipartUpload = (
    filePath: number,
    fileName: string,
    contentType: string,
    quantidadeDePartes: number,
    configs?: AxiosRequestConfig
  ): AxiosPromise<ArquivoMultipartUrl[]> => {
    return axios.post(
      `${
        this.resource
      }/arquivos/gerar-urls-multipart?filePath=${filePath}&fileName=${encodeURIComponent(
        fileName
      )}&contentType=${contentType}&quantidadeDePartes=${quantidadeDePartes}`,
      configs
    );
  };

  public enviarParteArquivoParaServidor = (
    parte: Blob,
    url: string,
    configs?: AxiosRequestConfig
  ) => {
    return axios.put(url, parte, configs);
  };

  public finalizarMultipartUpload = (
    fullPath: string,
    uploadId: string,
    partes: ArquivoParteUpload[],
    configs?: AxiosRequestConfig
  ): AxiosPromise<ArquivoUrl> => {
    return axios.post(
      `${this.resource}/arquivos/finalizar-multipart`,
      { fullPath, uploadId, partes },
      configs
    );
  };

  public abortarMultipartUpload = (
    fullPath: string,
    fileName: string,
    uploadId: string
  ): AxiosPromise<void> => {
    return axios.post(`${this.resource}/arquivos/abortar-multipart`, {
      fullPath,
      fileName,
      uploadId
    });
  };

  public receberArquivoDoServidor = (
    url: string,
    configs?: AxiosRequestConfig
  ) => {
    return axios.get(url, { responseType: 'arraybuffer', ...configs });
  };

  public salvarArquivoEntity = (
    entityId: string,
    arquivoEntity: any,
    configs?: AxiosRequestConfig
  ) => {
    return axios.post<ArquivoEntity>(
      `${this.resource}/${entityId}/arquivos`,
      arquivoEntity,
      configs
    );
  };

  public atualizarArquivoUrl = (
    entityId: string,
    idArquivoEntity: string,
    arquivoUrl: ArquivoUrl,
    configs?: AxiosRequestConfig
  ) => {
    return axios.put<ArquivoEntity>(
      `${this.resource}/${entityId}/arquivos/${idArquivoEntity}/url`,
      arquivoUrl,
      configs
    );
  };

  public deletarArquivo = (entityArquivoId: string) => {
    return axios.delete(`${this.resource}/arquivos/${entityArquivoId}`);
  };

  public atualizarArquivo = (
    entityId: string,
    arquivoEntity: ArquivoEntity
  ) => {
    return axios.put(`${this.resource}/${entityId}/arquivos`, arquivoEntity);
  };

  public gerarUrlParaDownload = (
    fullPath: string,
    fileName: string,
    configs?: AxiosRequestConfig
  ): AxiosPromise<ArquivoUrl> => {
    return axios.get(
      `${
        this.resource
      }/download?fullPath=${fullPath}&fileName=${encodeURIComponent(fileName)}`,
      configs
    );
  };

  public alterarSigilo = (
    entityArquivoId: string
  ): AxiosPromise<ArquivoEntity> => {
    return axios.put(
      `${this.resource}/arquivos/${entityArquivoId}/change-sigiloso`
    );
  };

  public changeTipoSigap = (
    entityArquivoId: string,
    tipoSigap: TipoSigapEnum
  ): AxiosPromise<ArquivoEntity> => {
    return axios.put(
      `${this.resource}/arquivos/${entityArquivoId}/change-tipo-sigap/${tipoSigap}`
    );
  };

  public reorder = (ids: string[]): AxiosPromise<ArquivoEntity> => {
    return axios.put(`${this.resource}/arquivos/reorder`, ids);
  };

  public salvarArquivoTemplate = (
    entityId: string,
    template: ModeloArquivoTemplateDTO
  ): AxiosPromise<ArquivoEntity> => {
    return axios.post(
      `${this.resource}/${entityId}/arquivos/template`,
      template
    );
  };

  public salvarArquivoTemplatePDF = (
    entityId: string,
    template: string,
    fileName: string
  ) => {
    const exporterParam = {
      type: 'PDF',
      fileName
    };

    return axios.post(`${this.resource}/${entityId}/arquivos/template/pdf`, {
      name: fileName,
      text: template,
      exporterParam
    });
  };
}
